// src/containers/Chat/ChatMessages.jsx
import React, { useState, useMemo } from 'react';
import MessageActionsMenu from './MessageActionsMenu';
import LightboxGallery from './../../components/media/LightboxGallery';
import Attachment from './Attachment';
import { resolveAnyStaticUrl } from '../../urlConfig';
import axiosInstance from '../../helpers/axios';
import { toggleStar } from '../../actions/chatActions';
import { useDispatch } from 'react-redux';

const labelCallStatus = (s) => {
    switch (s) {
        case 'missed': return 'пропущен';
        case 'ended': return 'завершён';
        case 'rejected': return 'отклонён';
        case 'busy': return 'занято';
        case 'failed': return 'ошибка';
        case 'cancelled': return 'отменён';
        default: return s || '—';
    }
};

function parseReply(msg) {
    if (msg.reply && (msg.reply.author || msg.reply.snippet)) {
        return {
            author: msg.reply.author || 'Собеседник',
            snippet: (msg.reply.snippet || '').trim(),
            body: (msg.text || '').trim(),
        };
    }
    const t = (msg.text || '');
    if (t.startsWith('↩️ ')) {
        const nl = t.indexOf('\n');
        const header = nl > -1 ? t.slice(0, nl) : t;
        const m = header.match(/^↩️\s+(.+?):\s+(.*)$/);
        return {
            author: m ? m[1] : 'Собеседник',
            snippet: m ? m[2] : header.replace(/^↩️\s*/, ''),
            body: nl > -1 ? t.slice(nl + 1) : '',
        };
    }
    return null;
}

export default function ChatMessages({
    messages,
    currentUserId,
    onDeleteMessage,
    onOpenReaders,
    onReply,
    selectedId
}) {
    const [locSel, setLocSel] = useState(null);
    const [gallery, setGallery] = useState({ open: false, images: [], start: 0 });
    const [tr, setTr] = useState({ open: false, text: '', loading: false });
    const dispatch = useDispatch();
    // Изображения для лайтбокса — универсальный резолвер, без склейки /public//media/...
    const allImages = useMemo(() => {
        const arr = (messages || [])
            .filter(m => {
                const url = (m.fileUrl || '').toLowerCase();
                const mime = (m.fileMime || '').toLowerCase();
                return m.fileUrl && (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url));
            })
            .map(m => ({
                id: m._id,
                src: resolveAnyStaticUrl(m.fileUrl),
                name: m.fileName || 'image'
            }));
        const seen = new Set();
        return arr.filter(it => (seen.has(it.src) ? false : (seen.add(it.src), true)));
    }, [messages]);

    const openLightbox = (src) => {
        const start = Math.max(0, allImages.findIndex(i => i.src === src));
        setGallery({ open: true, images: allImages, start });
    };

    // Расшифровка голосового (заглушка на сервере сейчас уже есть)
    const transcribe = async ({ href, fileName }) => {
        try {
            setTr({ open: true, text: '', loading: true });
            const r = await axiosInstance.post('/messages/voice/transcribe', { url: href, fileName });
            setTr({ open: true, text: r?.data?.text || '(пусто)', loading: false });
        } catch (e) {
            console.warn('transcribe error', e);
            setTr({ open: true, text: 'Ошибка распознавания', loading: false });
        }
    };

    return (
        <div className="chat-modern__messages">
            {(messages || []).map((msg) => {
                // 1) карточка звонка
                if (msg.type === 'call' || msg.call) {
                    const c = msg.call || {};
                    const outgoing = String(c.initiator) === String(currentUserId);
                    return (
                        <div className={`bubble call ${outgoing ? 'self' : 'other'}`} key={msg._id}>
                            <div className="bubble__head">
                                <div className="bubble__sender">{outgoing ? 'Вы' : 'Собеседник'}</div>
                            </div>
                            <p className="bubble__text">
                                {outgoing ? 'Исходящий' : 'Входящий'}
                                {c.video ? ' видеозвонок' : ' звонок'}
                                {typeof c.durationSec === 'number' &&
                                    ` • ${Math.floor((c.durationSec || 0) / 60)}:${String((c.durationSec || 0) % 60).padStart(2, '0')}`}
                                {c.status && ` • ${labelCallStatus(c.status)}`}
                            </p>
                            <div className="bubble__meta">
                                <span className="bubble__time">
                                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </div>
                    );
                }

                // 2) обычные сообщения
                const isSelf = (msg.sender?._id || msg.sender) === currentUserId;
                const readByIds = (msg.readBy || []).map(u => (u?._id || u).toString());
                const isReadByMe = readByIds.includes(String(currentUserId));
                const someoneRead = (msg.readBy || []).length > 0;

                const reply = parseReply(msg);
                const bodyText = reply ? reply.body : (msg.text || '');

                const cls = [
                    'bubble',
                    isSelf ? 'self' : 'other',
                    selectedId && selectedId === msg._id ? 'is-selected' : '',
                    locSel === msg._id ? 'is-selected' : '',
                ].join(' ');

                const onDouble = () => { setLocSel(msg._id); onReply?.(msg); };

                return (
                    <div className={cls} key={msg._id} onDoubleClick={onDouble}>
                        <div className="bubble__head">
                            <div className="bubble__sender">{isSelf ? 'Вы' : (msg.sender?.firstName || 'Собеседник')}</div>
                            <div className="bubble__menu">

                                <MessageActionsMenu
                                    openToRight={isSelf}
                                    onReply={() => onReply?.(msg)}
                                    onViewReads={() => onOpenReaders?.(msg.readBy || [])}
                                    onCopy={async () => {
                                        const text = msg.text || (msg.fileName ? `Файл: ${msg.fileName}` : '');
                                        if (!text) return;
                                        try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
                                    }}
                                    onDelete={isSelf ? () => onDeleteMessage?.(msg._id) : undefined}
                                    onStarToggle={(on) => dispatch(toggleStar(msg._id, currentUserId, on))}
                                    starred={Array.isArray(msg.starredBy) && msg.starredBy.some(u => String(u?._id || u) === String(currentUserId))}
                                />

                            </div>
                        </div>

                        {reply && (
                            <div className="bubble__quote">
                                <div className="bubble__quote-author">{reply.author}</div>
                                <div className="bubble__quote-text">{reply.snippet}</div>
                            </div>
                        )}

                        {!!bodyText && <p className="bubble__text">{bodyText}</p>}

                        {/* вложение */}
                        {msg.fileUrl && (
                            <Attachment
                                key={`${msg._id}-att`}
                                message={msg}
                                onOpenImage={openLightbox}
                                onTranscribe={transcribe}
                            />
                        )}

                        <div className="bubble__meta">
                            <span className="bubble__status">
                                {isSelf ? (someoneRead ? '✔✔' : '✔') : (isReadByMe ? '✔✔' : '✔')}
                            </span>
                            <span className="bubble__time">
                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </div>
                );
            })}

            {/* Галерея */}
            {gallery.open && (
                <LightboxGallery
                    open={gallery.open}
                    images={gallery.images}
                    startIndex={gallery.start}
                    onClose={() => setGallery({ open: false, images: [], start: 0 })}
                />
            )}

            {/* Модалка расшифровки */}
            {tr.open && (
                <div className="modal" onClick={() => setTr({ open: false, text: '', loading: false })}>
                    <div className="modal__body" onClick={(e) => e.stopPropagation()}>
                        <div className="modal__head">
                            <div className="modal__title">Расшифровка</div>
                            <button onClick={() => setTr({ open: false, text: '', loading: false })}>✕</button>
                        </div>
                        <div className="modal__content">
                            {tr.loading ? 'Распознаём…' : <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{tr.text}</pre>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
